import autobind from 'autobind-decorator';
import classNames from 'classnames';
import React, { Component } from 'react';
import Dropzone, { ImageFile } from 'react-dropzone';
import Button from 'react-progress-button';

import './image-upload-zone.scss';

import { fetchProtectedImageAsDataUrl } from 'common/utils/image-utils';

import { CommonsApi } from '../../../../api/common';

enum ButtonLoadStates {
  none = '',
  success = 'success',
  error = 'error',
  loading = 'loading',
}

interface State {
  imagePreviewDataUrl: string | null;
  defaultPreviewDataUrl: string | null;
  loadState: ButtonLoadStates;
  displayNotification: boolean;
  errorMessage: string;
}

interface Props {
  previewUrl: string;
  defaultImagePreviewUrl?: string;
  deleteImage: () => void;
  // todo typedef
  onLoadEnd: (err: any, response?: any) => void;
  imageInterceptor: (file: File) => void;
  size?: string;
}

export class ImageUploadZone extends Component<Props, State> {
  private input: HTMLInputElement;

  constructor(props: Props) {
    super(props);
    this.state = {
      imagePreviewDataUrl: null,
      defaultPreviewDataUrl: null,
      loadState: ButtonLoadStates.none,
      displayNotification: false,
      errorMessage: '',
    };
  }

  public componentDidMount(): void {
    document.addEventListener('dragover', this.preventDefault, false);
    document.addEventListener('drop', this.preventDefault, false);
    this.loadDefaultPreview(this.props.defaultImagePreviewUrl);
  }

  public componentDidUpdate(prevProps: Props): void {
    if (prevProps.defaultImagePreviewUrl !== this.props.defaultImagePreviewUrl) {
      this.loadDefaultPreview(this.props.defaultImagePreviewUrl);
    }
  }

  @autobind
  public upload(blob: Blob): void {
    const { onLoadEnd } = this.props;
    this.setState({ loadState: ButtonLoadStates.loading });
    if (!blob.type.match('image.*')) {
      const err = {
        message: 'file type error',
        type: blob.type,
        fileName: 'ImagesUploader',
      };
      if (onLoadEnd) {
        onLoadEnd(err);
      }
      this.setState({ loadState: ButtonLoadStates.error });
    } else {
      this.uploadImages(blob, onLoadEnd);
    }
  }

  public render(): React.ReactNode {
    const { loadState, imagePreviewDataUrl, defaultPreviewDataUrl } = this.state;

    return (
      <div className='image-uploader'>
        <div className='image-uploader__files-input-container'>
          <div
            className={classNames(
              'image-uploader__load-container',
              { [`image-uploader__load-container--${this.props.size}`]: !!this.props.size },
            )}
          >
            { (imagePreviewDataUrl || defaultPreviewDataUrl) && (
                <div
                  className={'image-uploader__delete-preview'}
                  onClick={this.onDeletePreviewClick}
                />
            )}
            <Dropzone
              onDrop={this.handleFileDrop}
              accept='image/*'
              className='image-uploader__dropzone'
              disabled={false}
            >
              <Button
                controlled={true}
                state={loadState}
                onClick={this.openFileDialogClick}
                type='button'
                classNamespace='image-uploader__button-'
                className='image-uploader__pseudo-button'
                onMouseOver={this.showNotification}
                onMouseLeave={this.hideNotification}
                onDragOver={this.showNotification}
                onDragLeave={this.hideNotification}
              >
                {this.buildButtonContent()}
              </Button>
            </Dropzone>
          </div>
          <input
            name={'filesInput'}
            id={'filesInput'}
            className='image-uploader__file-input'
            ref={this.saveInputRef}
            type='file'
            accept='image/*'
            disabled={loadState === 'loading'}
            onChange={this.handleImageChange}
          />
        </div>
        {this.state.errorMessage ? <span className='image-uploader__error'>{this.state.errorMessage}</span> : null}
      </div>
    );
  }

  public componentWillUnmount(): void {
    document.removeEventListener('dragover', this.preventDefault);
    document.removeEventListener('drop', this.preventDefault);
  }

  private preventDefault(event: DragEvent): void {
    event.preventDefault();
  }

  @autobind
  private saveInputRef(input: HTMLInputElement): void {
    this.input = input;
  }

  @autobind
  private hideNotification(): void {
    this.setState({ displayNotification: false });
  }

  @autobind
  private onDeletePreviewClick(e: React.MouseEvent<HTMLDivElement>): void {
    e.preventDefault();
    this.setState({ imagePreviewDataUrl: null, defaultPreviewDataUrl: null });
    this.props.deleteImage();
  }

  @autobind
  private handleFileDrop(files: ImageFile[]): void {
    if (files.length && files[0]) {
      this.props.imageInterceptor(files[0]);
    }
  }

  @autobind
  private handleImageChange(e: React.ChangeEvent<HTMLInputElement>): void {
    e.preventDefault();
    const filesList = e.target.files;
    if (filesList.length && filesList[0]) {
      this.props.imageInterceptor(filesList[0]);
    }
  }

  @autobind
  private openFileDialogClick(e: React.MouseEvent<HTMLInputElement>): void {
    e.preventDefault();
    e.stopPropagation();
    if (this.input) {
      this.input.value = '';
      this.input.click();
    }
  }

  @autobind
  private showNotification(): void {
    const { imagePreviewDataUrl } = this.state;
    if (imagePreviewDataUrl) {
      this.setState({ displayNotification: true });
    }
  }

  @autobind
  private buildButtonContent(): React.ReactNode {
    const { imagePreviewDataUrl, defaultPreviewDataUrl } = this.state;
    const previewDataUrl = imagePreviewDataUrl || defaultPreviewDataUrl;
    if (!previewDataUrl) {
      return (<span className='image-uploader__pseudo-button-content' />);
    }
    const imgPreviewStyle: React.CSSProperties = {
      backgroundImage: `url(${previewDataUrl})`,
    };
    return (
      <div className={'image-uploader__preview'} style={imgPreviewStyle}>
        {this.state.displayNotification && <div className='image-uploader__notification'/>}
      </div>
    );
  }

  private loadDefaultPreview(previewPath?: string): void {
    if (!previewPath) {
      this.setState({ defaultPreviewDataUrl: null });
      return;
    }

    fetchProtectedImageAsDataUrl(previewPath).then(dataUrl => {
      this.setState({ defaultPreviewDataUrl: dataUrl });
    });
  }

  private loadUploadedPreview(previewPath: string): void {
    fetchProtectedImageAsDataUrl(previewPath).then(dataUrl => {
      this.setState({ imagePreviewDataUrl: dataUrl });
    });
  }

  private uploadImages(file: Blob, onLoadEnd: (error: any, response?: string) => void): void {
    CommonsApi.uploadFileWithoutProgressTracking(file as File).then(responseData => {
      const [imageUrl] = responseData;
      const previewPath = `${this.props.previewUrl}/${imageUrl}`;
      this.setState({ loadState: ButtonLoadStates.success });
      this.loadUploadedPreview(previewPath);
      onLoadEnd(null, imageUrl);
    }).catch(error => {
      console.error(error);
      const err = {
        message: 'invalid response type',
        error,
      };
      this.setState({ loadState: ButtonLoadStates.error });
      onLoadEnd(err);
    });
  }
}
